import {
  ChevronRightIcon,
  PaintBrushIcon,
  PhotoIcon,
  RectangleGroupIcon,
  RectangleStackIcon,
  Squares2X2Icon,
  SwatchIcon
} from "@heroicons/react/24/outline"
import React, { useState } from "react"

// Import shape icons for preview
import shapeRoundIcon from "../../../../assets/images/builder/v2/shape-round.svg"
import shapeSharpIcon from "../../../../assets/images/builder/v2/shape-sharp.svg"
import shapeSoftIcon from "../../../../assets/images/builder/v2/shape-soft.svg"
import Typography from "../../../components/shared/Typography"
import { SHAPE_OPTIONS } from "../constants/shapeConstants"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { formatFontFamily } from "../utils/fontUtils"
import { useWebsiteBuilderV2 } from "../WebsiteBuilderV2Context"

const SitewideStylesForm = () => {
  const { theme, setShowEditSitewideStyleScreen, setEditingSitewideStyle } = useWebsiteBuilderV2()

  // Force re-render when theme changes
  const [previewKey, setPreviewKey] = useState(0)
  React.useEffect(() => {
    setPreviewKey((prev) => prev + 1)
  }, [theme?.colors?.selectedPreset, theme?.colors?.primary])

  // Helper function to ensure we always have valid colors with fallbacks
  const ensureValidColors = (colors) => {
    if (!colors || typeof colors !== "object") {
      // Return default colors if invalid
      return generateColorPalette("#BD654F") // Default earthy theme
    }

    // Ensure all required colors exist with fallbacks
    const defaultColors = generateColorPalette("#BD654F")
    return {
      primary: colors.primary || defaultColors.primary,
      primaryDark: colors.primaryDark || defaultColors.primaryDark,
      secondary: colors.secondary || defaultColors.secondary,
      accent: colors.accent || defaultColors.accent,
      background: colors.background || defaultColors.background,
      bgDark: colors.bgDark || defaultColors.bgDark,
      bgMedium: colors.bgMedium || defaultColors.bgMedium,
      bgLight: colors.bgLight || defaultColors.bgLight,
      bgExtraLight: colors.bgExtraLight || defaultColors.bgExtraLight,
      textDark: colors.textDark || defaultColors.textDark,
      textLight: colors.textLight || defaultColors.textLight,
      strokeMedium: colors.strokeMedium || defaultColors.strokeMedium,
      promoBannerBackground: colors.promoBannerBackground || defaultColors.promoBannerBackground,
      promoBannerText: colors.promoBannerText || defaultColors.promoBannerText,
      promoBannerLink: colors.promoBannerLink || defaultColors.promoBannerLink,
      reviewStars: colors.reviewStars || "#FF9529" // Use exact color from Figma
    }
  }

  // Using enhanced color palette generation from utils

  // Color presets for preview - using formula-generated colors
  const colorPresets = [
    {
      id: "earthy",
      colors: generateColorPalette("#BD654F")
    },
    {
      id: "zen",
      colors: generateColorPalette("#97854A")
    },
    {
      id: "tranquility",
      colors: generateColorPalette("#5991B7")
    },
    {
      id: "radiance",
      colors: generateColorPalette("#C5782D")
    },
    {
      id: "vitality",
      colors: generateColorPalette("#BA1457")
    },
    {
      id: "awaken",
      colors: generateColorPalette("#E66565")
    },
    {
      id: "flourish",
      colors: generateColorPalette("#627654")
    },
    {
      id: "harmony",
      colors: generateColorPalette("#3F978E")
    },
    {
      id: "balance",
      colors: generateColorPalette("#234188")
    },
    {
      id: "serenity",
      colors: generateColorPalette("#735097")
    }
  ]

  // Font pairs for preview - matching Figma design
  const fontPairs = [
    {
      id: "playfair-work",
      primary: "Playfair Display",
      secondary: "Work Sans",
      primaryFont: "Playfair Display, serif",
      secondaryFont: "Work Sans, sans-serif"
    },
    {
      id: "inter-libre",
      primary: "Inter",
      secondary: "Libre Franklin",
      primaryFont: "Inter, sans-serif",
      secondaryFont: "Libre Franklin, sans-serif"
    },
    {
      id: "poppins-raleway",
      primary: "Poppins",
      secondary: "Raleway",
      primaryFont: "Poppins, sans-serif",
      secondaryFont: "Raleway, sans-serif"
    },
    {
      id: "montserrat-lato",
      primary: "Montserrat",
      secondary: "Lato",
      primaryFont: "Montserrat, sans-serif",
      secondaryFont: "Lato, sans-serif"
    },
    {
      id: "opensans-sourcesans",
      primary: "Open Sans",
      secondary: "Source Sans Pro",
      primaryFont: "Open Sans, sans-serif",
      secondaryFont: "Source Sans Pro, sans-serif"
    },
    {
      id: "arima-madurai",
      primary: "Arima Madurai",
      secondary: "Mulish",
      primaryFont: "Arima Madurai, sans-serif",
      secondaryFont: "Mulish, sans-serif"
    },
    {
      id: "cabin-cabin",
      primary: "Cabin",
      secondary: "Cabin",
      primaryFont: "Cabin, sans-serif",
      secondaryFont: "Cabin, sans-serif"
    },
    {
      id: "ebgaramond-splinesans",
      primary: "EB Garamond",
      secondary: "Spline Sans",
      primaryFont: "EB Garamond, serif",
      secondaryFont: "Spline Sans, sans-serif"
    },
    {
      id: "jost-inter",
      primary: "Jost",
      secondary: "Inter",
      primaryFont: "Jost, sans-serif",
      secondaryFont: "Inter, sans-serif"
    },
    {
      id: "nunito-nunitosans",
      primary: "Nunito",
      secondary: "Nunito Sans",
      primaryFont: "Nunito, sans-serif",
      secondaryFont: "Nunito Sans, sans-serif"
    }
  ]

  // Shape options for preview - using centralized constants
  const shapeOptions = SHAPE_OPTIONS.map((option) => ({
    ...option,
    icon: option.id === "sharp" ? shapeSharpIcon : option.id === "soft" ? shapeSoftIcon : shapeRoundIcon
  }))

  // Helper functions to find current selections
  const findCurrentColorPreset = () => {
    if (theme?.colors?.selectedPreset) {
      return colorPresets.find((preset) => preset.id === theme.colors.selectedPreset)
    }

    // If no preset is selected, check if we have custom colors
    if (theme?.colors?.customHex) {
      return null // Custom colors
    }

    // Ensure we have valid colors with fallbacks
    const validColors = ensureValidColors(theme?.colors)

    // Try to match current colors with a preset
    const currentColors = {
      primary: validColors.primary,
      primaryDark: validColors.primaryDark,
      secondary: validColors.secondary,
      accent: validColors.accent,
      background: validColors.background
    }

    return colorPresets.find((preset) => {
      const presetColors = preset.colors
      if (!presetColors || typeof presetColors !== "object") return false

      return Object.keys(currentColors).every((key) => {
        const currentColor = currentColors[key]
        const presetColor = presetColors[key]
        if (!currentColor || !presetColor) return false
        const normalizedCurrent = currentColor.replace("#", "").toUpperCase()
        const normalizedPreset = presetColor.replace("#", "").toUpperCase()
        return normalizedCurrent === normalizedPreset
      })
    })
  }

  // Default to first pair
  const findCurrentFontPair = () =>
    fontPairs.find(
      (pair) => pair.primaryFont === theme?.fonts?.heading && pair.secondaryFont === theme?.fonts?.paragraph
    ) || fontPairs[0]

  // Default to "soft", then first option if the array is ever empty
  const findCurrentShape = () =>
    shapeOptions.find(
      (option) => option.borderRadius === theme?.borderRadius && option.buttonStyle === theme?.buttonStyle
    ) ||
    shapeOptions[1] ||
    shapeOptions[0]

  const sections = [
    {
      id: "color-theme",
      title: "Color Theme",
      icon: <SwatchIcon className="h-5 w-5" />,
      preview: () => {
        try {
          // First check if there's a selected preset
          let currentPreset = null
          if (theme?.colors?.selectedPreset) {
            currentPreset = colorPresets.find((preset) => preset.id === theme.colors.selectedPreset)
          }

          // If no selected preset, try to find current preset based on colors
          if (!currentPreset) {
            currentPreset = findCurrentColorPreset()
          }

          if (currentPreset && currentPreset.colors && typeof currentPreset.colors === "object") {
            // Show only the most representative colors for preview
            // Use the same colors as ColorThemeForm.jsx for consistency
            const representativeColors = [
              currentPreset.colors.primary,
              currentPreset.colors.bgDark,
              currentPreset.colors.bgMedium,
              currentPreset.colors.bgLight,
              currentPreset.colors.bgExtraLight
            ].filter((color) => color && typeof color === "string")

            if (representativeColors.length > 0) {
              return (
                <div key={previewKey} className="flex items-center space-x-[-8px]">
                  {representativeColors.map((color, index) => (
                    <div
                      key={index}
                      className="h-7 w-7 rounded-full border-2 border-white shadow-sm"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )
            }
          }

          // Fallback to custom colors or default
          const validColors = ensureValidColors(theme?.colors)
          const fallbackColors = [
            validColors.primary,
            validColors.bgDark,
            validColors.bgMedium,
            validColors.bgLight,
            validColors.bgExtraLight
          ].filter((color) => color && typeof color === "string")

          return (
            <div key={previewKey} className="flex items-center space-x-[-8px]">
              {fallbackColors.map((color, index) => (
                <div
                  key={index}
                  className="h-7 w-7 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          )
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("Error rendering color preview:", error)
          // Return default color preview with representative colors
          const defaultColors = generateColorPalette("#BD654F")
          const defaultColorArray = [
            defaultColors.primary,
            defaultColors.bgDark,
            defaultColors.bgMedium,
            defaultColors.bgLight,
            defaultColors.bgExtraLight
          ]

          return (
            <div key={previewKey} className="flex items-center space-x-[-8px]">
              {defaultColorArray.map((color, index) => (
                <div
                  key={index}
                  className="h-7 w-7 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          )
        }
      }
    },
    {
      id: "fonts",
      title: "Fonts",
      icon: <PaintBrushIcon className="h-5 w-5" />,
      preview: () => {
        const currentPair = findCurrentFontPair()
        return (
          <div className="flex items-end space-x-1">
            <span
              className="text-2xl font-bold leading-none"
              style={{
                fontFamily: formatFontFamily(currentPair.primaryFont),
                fontWeight: 700
              }}>
              Ag
            </span>
            <span
              className="text-base leading-none"
              style={{
                fontFamily: formatFontFamily(currentPair.secondaryFont),
                fontWeight: currentPair.secondaryFont.includes("Raleway") ? 300 : 400
              }}>
              Ag
            </span>
          </div>
        )
      }
    },
    {
      id: "shape",
      title: "Shape",
      icon: <Squares2X2Icon className="h-5 w-5" />,
      preview: () => {
        const currentShape = findCurrentShape()
        if (!currentShape?.icon) {
          return <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-200 p-2" />
        }
        return (
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-200 p-2">
            <img src={currentShape.icon} alt={currentShape.name ?? "Shape"} className="h-3 w-3" />
          </div>
        )
      }
    },
    {
      id: "logo",
      title: "Logo",
      icon: <PhotoIcon className="h-5 w-5" />,
      preview: () => {
        const header = theme?.header || {}
        const mainLine = header.textLogo?.mainLine
        if (header.logoType === "text" && mainLine) {
          return (
            <span
              className="text-sm font-medium text-gray-dark"
              style={{
                fontFamily: `'${header.textLogo?.mainFont || "Inter"}', sans-serif`
              }}>
              {mainLine.slice(0, 12)}
              {mainLine.length > 12 ? "…" : ""}
            </span>
          )
        }
        if (header.logo) {
          return <img src={header.logo} alt="Logo" className="h-7 w-auto max-w-[60px] object-contain" />
        }
        return <span className="text-sm text-gray-500">Not set</span>
      }
    },
    {
      id: "header",
      title: "Header",
      icon: <RectangleGroupIcon className="h-5 w-5" />
    },
    {
      id: "footer",
      title: "Footer",
      icon: <RectangleStackIcon className="h-5 w-5" />
    }
  ]

  // Always show Logo section so users can change image logos; text logo is optional when textLogoBuilderEnabled
  const visibleSections = sections

  // Utility function to scroll to header or footer
  const scrollToHeaderOrFooter = (type) => {
    const elementId = type === "header" ? "section-header-header-section" : "section-footer-footer-section"
    const element = document.getElementById(elementId)

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      })

      // Add a temporary highlight effect
      element.style.transition = "box-shadow 0.3s ease"
      element.style.boxShadow = "0 0 0 4px rgba(20, 184, 166, 0.3)"

      setTimeout(() => {
        element.style.boxShadow = ""
      }, 2000)
    }
  }

  const handleSectionSelect = (sectionId) => {
    // Set the editing sitewide style and show the edit screen
    setEditingSitewideStyle(sectionId)
    setShowEditSitewideStyleScreen(true)

    // If selecting header or footer, scroll to it
    if (sectionId === "header" || sectionId === "footer") {
      scrollToHeaderOrFooter(sectionId)
    }
  }

  // Show the accordion items when no section is selected
  return (
    <div className="space-y-0 overflow-hidden rounded-lg border border-gray-light">
      {visibleSections.map((section) => (
        <button
          key={section.id}
          className="flex w-full items-center justify-between border-b border-gray-light px-8 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-ultralight"
          onClick={() => handleSectionSelect(section.id)}>
          <div className="flex items-center">
            <Typography variant="body" className="font-medium text-gray-dark">
              {section.title}
            </Typography>
          </div>
          <div className="flex items-center space-x-3">
            {section.preview && section.preview()}
            <ChevronRightIcon className="h-5 w-5 text-gray" />
          </div>
        </button>
      ))}
    </div>
  )
}

export default SitewideStylesForm
